import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, IbanElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Tag, X, Check } from "lucide-react";
import {
  AKTION_ENDE_LABEL,
  getBundlePricingBySlug,
  formatEuro,
  type BundleTarif,
  type BundleZahlart,
} from "@/config/trainingBundlePricing";
import { RUHEN_TITEL_KASSE, ruhenSatzKasse, TESTPHASE_TITEL, testphaseSatzKasse } from "@/lib/aboRuhen";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_ABO);

const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ["SEPA"],
  placeholderCountry: "DE",
  style: {
    base: {
      fontSize: "16px",
      color: "#1a1a2e",
      fontFamily: "system-ui, -apple-system, sans-serif",
      "::placeholder": { color: "#94a3b8" },
      lineHeight: "40px",
    },
    invalid: { color: "#ef4444", iconColor: "#ef4444" },
  },
};

interface FormState {
  email: string;
  first_name: string;
  last_name: string;
  account_holder: string;
  company: string;
  street: string;
  line2: string;
  postal_code: string;
  city: string;
}

const EMPTY_FORM: FormState = {
  email: "",
  first_name: "",
  last_name: "",
  account_holder: "",
  company: "",
  street: "",
  line2: "",
  postal_code: "",
  city: "",
};

interface PriceBlock { amountCents: number; daysUntilDue: number }
interface RatenBlock { totalCents: number; installmentCount: number; baseInstallmentCents: number; finalAdjustmentCents: number }
/**
 * Fachkraft für Musik und Kita-Gitarrenkurs: Ein Mitglied mit eigenem Stripe-Abo, das beim Buchen 12 Monate ruht
 * (Frage 355). Nur der Server entscheidet; die Seite sagt es vor dem Knopf und schickt dann `abo_ruhen` mit.
 */
interface AboRuhenInfo {
  bis: string;
  monate: number;
}
/** Testphase der Mitgliedschaft, die beim Buchen zu ihrem Ende ausläuft (Frage 360). */
interface TestphaseInfo {
  bis: string;
}
/**
 * Befristete Frühbucher-Aktion, wie sie `detect_tariff` zurückgibt.
 *
 * `aktiv` entscheidet die SERVERZEIT in `create-bundle-checkout`
 * (`_shared/fruehbucher-aktion.ts`). Der Checkout rechnet nicht selbst nach —
 * er zeigt nur, was der Server bestätigt hat. `regulaer` ist der
 * Vergleichspreis für die Durchstreichung.
 */
interface AktionInfo {
  aktiv: boolean;
  endeIso: string;
  regulaer: { rechnung: PriceBlock; raten: RatenBlock };
}
interface TariffDetection {
  tarif: BundleTarif;
  is_authenticated: boolean;
  rechnung: PriceBlock;
  raten: RatenBlock;
  aktion?: AktionInfo;
  promoApplied?: boolean;
  standardPrices?: { rechnung: PriceBlock; raten: RatenBlock };
  aboRuhen?: AboRuhenInfo | null;
  testphaseEndet?: TestphaseInfo | null;
}

const TARIF_LABELS: Record<BundleTarif, string> = {
  mitglied: "als aktives MusiKonzept-Mitglied",
  extern: "als externe:r Teilnehmer:in",
  standard: "zum Standardpreis",
  aktion: "zum Webinar-Aktionspreis",
};

function BundleKaufenInner() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const stripe = useStripe();
  const elements = useElements();

  const pricing = getBundlePricingBySlug(slug);

  const [bundleActive, setBundleActive] = useState<boolean | null>(null);
  const [detection, setDetection] = useState<TariffDetection | null>(null);
  const [detectionError, setDetectionError] = useState<string | null>(null);
  const [zahlart, setZahlart] = useState<BundleZahlart>("raten");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [ibanComplete, setIbanComplete] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);

  // Promo-Code (URL-Param ?code=… wird vorbefüllt & automatisch eingelöst)
  const initialCode = (searchParams.get("code") ?? "").trim().toUpperCase();
  const [couponInput, setCouponInput] = useState(initialCode);
  const [appliedCode, setAppliedCode] = useState<string>(initialCode);
  const [couponLoading, setCouponLoading] = useState(false);

  const submissionIdRef = useRef<string>(crypto.randomUUID());
  const submittingRef = useRef(false);

  // Bundle-Aktivität
  useEffect(() => {
    if (!pricing) { setBundleActive(false); return; }
    let cancelled = false;
    supabase
      .from("bundles")
      .select("is_active")
      .eq("id", pricing.bundleId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setBundleActive(data?.is_active ?? false);
      });
    return () => { cancelled = true; };
  }, [pricing]);

  // Tarif & Preise laden (reaktiv auf appliedCode)
  useEffect(() => {
    if (!pricing || bundleActive !== true) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const u = sess.session?.user;
        if (u) {
          const md = (u.user_metadata ?? {}) as Record<string, unknown>;
          setForm((f) => ({
            ...f,
            email: f.email || u.email || "",
            first_name: f.first_name || (typeof md.first_name === "string" ? md.first_name : ""),
            last_name: f.last_name || (typeof md.last_name === "string" ? md.last_name : ""),
          }));
        }

        const { data, error } = await supabase.functions.invoke("create-bundle-checkout", {
          body: { action: "detect_tariff", bundle_id: pricing.bundleId, promoCode: appliedCode || undefined },
        });
        if (cancelled) return;
        if (error) throw error;
        setDetection(data as TariffDetection);
      } catch (err) {
        if (!cancelled) setDetectionError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [pricing, bundleActive, appliedCode]);

  // Kontoinhaber aus Vor-/Nachname vorbelegen
  useEffect(() => {
    const full = `${form.first_name} ${form.last_name}`.trim();
    if (full && !form.account_holder) {
      setForm((f) => ({ ...f, account_holder: full }));
    }
  }, [form.first_name, form.last_name, form.account_holder]);

  const applyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    setAppliedCode(code);
    // detect_tariff-Effekt kümmert sich um das Reload → Loading-Ende dort implizit
    setTimeout(() => setCouponLoading(false), 400);
  };
  const removeCoupon = () => {
    setCouponInput("");
    setAppliedCode("");
  };

  if (!pricing) {
    return (
      <div className="container mx-auto py-20 text-center">
        <h1 className="text-2xl font-semibold">Ausbildung nicht gefunden</h1>
        <Button className="mt-6" onClick={() => navigate("/")}>Zur Startseite</Button>
      </div>
    );
  }
  if (bundleActive === null) {
    return <div className="container mx-auto py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }
  if (!bundleActive) {
    return (
      <div className="container mx-auto py-20 max-w-2xl text-center space-y-4">
        <SEO title="Buchung derzeit nicht verfügbar" description="Diese Ausbildung ist aktuell nicht buchbar." path={`/ausbildung/${slug}/kaufen`} />
        <h1 className="text-2xl font-semibold">Buchung derzeit nicht verfügbar</h1>
        <p className="text-muted-foreground">
          Bei Fragen melde dich gern unter <a className="underline" href="mailto:info@musikonzept.de">info@musikonzept.de</a>.
        </p>
      </div>
    );
  }
  if (detectionError) {
    return (
      <div className="container mx-auto py-20 max-w-2xl text-center space-y-4">
        <h1 className="text-2xl font-semibold">Preis konnte nicht ermittelt werden</h1>
        <p className="text-muted-foreground">{detectionError}</p>
      </div>
    );
  }
  if (!detection) {
    return <div className="container mx-auto py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  const updateField = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const rechnungAmount = detection.rechnung.amountCents;
  const ratenTotal = detection.raten.totalCents;
  const baseInst = detection.raten.baseInstallmentCents;
  const lastInst = baseInst + detection.raten.finalAdjustmentCents;
  const installmentCount = detection.raten.installmentCount;
  const ratenDescription = lastInst === baseInst
    ? `${installmentCount} × ${formatEuro(baseInst)} per SEPA-Lastschrift (gesamt ${formatEuro(ratenTotal)}, ohne Aufschlag).`
    : `${installmentCount - 1} × ${formatEuro(baseInst)} + 1 × ${formatEuro(lastInst)} per SEPA-Lastschrift (gesamt ${formatEuro(ratenTotal)}, ohne Aufschlag).`;
  // Frühbucher-Aktion: nur anzeigen, was der Server bestätigt hat. Fehlt der
  // Block oder steht `aktiv: false`, bleibt der Checkout wie vorher.
  const aktionAktiv = detection.aktion?.aktiv === true;
  const aktionRegulaerRechnung = aktionAktiv ? detection.aktion!.regulaer.rechnung.amountCents : null;
  const aktionRegulaerRatenTotal = aktionAktiv ? detection.aktion!.regulaer.raten.totalCents : null;
  const fruehbucherRabatt = aktionRegulaerRechnung !== null
    ? Math.max(0, aktionRegulaerRechnung - rechnungAmount)
    : 0;
  const zeigtFruehbucher = fruehbucherRabatt > 0;
  const promoApplied = detection.promoApplied === true;
  const stdRechnung = detection.standardPrices?.rechnung.amountCents;
  const stdRatenTotal = detection.standardPrices?.raten.totalCents;
  const aboRuhen = detection.aboRuhen ?? null;
  const testphaseEndet = detection.testphaseEndet ?? null;
  /** Hängt das Ende von Ruhezeit oder Testphase an die Dankeseite (`?ruht=`, `?testende=`). */
  const dankeZusatz = (d: { abo_ruht_bis?: unknown; testphase_endet_bis?: unknown } | null | undefined) =>
    (typeof d?.abo_ruht_bis === "string" ? `&ruht=${encodeURIComponent(d.abo_ruht_bis)}` : "") +
    (typeof d?.testphase_endet_bis === "string" ? `&testende=${encodeURIComponent(d.testphase_endet_bis)}` : "");
  const istMitgliedspreis = detection.tarif === "mitglied" && !!stdRechnung && stdRechnung !== rechnungAmount;
  const loginRedirect = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;

  const validate = (): boolean => {
    if (!agbAccepted) {
      toast({ title: "Bitte AGB akzeptieren", description: "Bitte bestätige, dass du AGB und Datenschutzerklärung gelesen hast.", variant: "destructive" });
      return false;
    }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast({ title: "E-Mail fehlt", description: "Bitte gültige E-Mail eingeben.", variant: "destructive" });
      return false;
    }
    if (!form.first_name || !form.last_name) {
      toast({ title: "Name fehlt", description: "Bitte Vor- und Nachnamen eingeben.", variant: "destructive" });
      return false;
    }
    if (zahlart === "rechnung") {
      if (!form.street || !form.postal_code || !form.city) {
        toast({ title: "Adresse unvollständig", description: "Bitte Straße, PLZ und Ort eingeben.", variant: "destructive" });
        return false;
      }
    } else {
      if (!form.account_holder.trim()) {
        toast({ title: "Kontoinhaber fehlt", description: "Bitte Namen des Kontoinhabers eingeben.", variant: "destructive" });
        return false;
      }
      if (!ibanComplete) {
        toast({ title: "IBAN fehlt", description: "Bitte deine IBAN eingeben.", variant: "destructive" });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);

    try {
      if (zahlart === "raten") {
        if (!stripe || !elements) throw new Error("Stripe noch nicht geladen");
        const ibanEl = elements.getElement(IbanElement);
        if (!ibanEl) throw new Error("IBAN-Feld nicht gefunden");

        const { paymentMethod: pm, error: pmError } = await stripe.createPaymentMethod({
          type: "sepa_debit",
          sepa_debit: ibanEl,
          billing_details: { name: form.account_holder, email: form.email },
        });
        if (pmError) throw new Error(pmError.message || "IBAN konnte nicht verarbeitet werden");

        const { data, error } = await supabase.functions.invoke("create-bundle-checkout", {
          body: {
            action: "checkout",
            bundle_id: pricing.bundleId,
            zahlart: "raten",
            paymentMethodId: pm.id,
            email: form.email,
            first_name: form.first_name,
            last_name: form.last_name,
            promoCode: appliedCode || undefined,
            abo_ruhen: aboRuhen ? true : undefined,
            testphase_beenden: testphaseEndet ? true : undefined,
            submission_id: submissionIdRef.current,
          },
        });
        if (error) {
          let backendMessage: string | null = null;
          try {
            const ctx = (error as { context?: Response }).context;
            if (ctx && typeof ctx.json === "function") {
              const parsed = await ctx.clone().json();
              if (parsed && typeof parsed.error === "string") backendMessage = parsed.error;
            }
          } catch { /* noop */ }
          throw new Error(backendMessage || error.message || "Zahlung fehlgeschlagen");
        }
        if (data?.error) throw new Error(data.error);

        if (data?.clientSecret) {
          const confirmParams = { payment_method: pm.id, return_url: `${window.location.origin}/ausbildung/${pricing.slug}/kauf-erfolgreich?bundle_id=${pricing.bundleId}` };
          const result = data.intentType === "setup"
            ? await stripe.confirmSepaDebitSetup(data.clientSecret, { payment_method: pm.id })
            : await stripe.confirmSepaDebitPayment(data.clientSecret, { payment_method: pm.id });
          if (result.error) throw new Error(result.error.message || "SEPA-Mandat konnte nicht bestätigt werden");
          void confirmParams; // reserved (return_url only relevant for redirect flows)
        }

        navigate(`/ausbildung/${pricing.slug}/kauf-erfolgreich?bundle_id=${encodeURIComponent(pricing.bundleId)}${dankeZusatz(data)}`);
        return;
      }

      // Rechnungs-Zweig (unverändert)
      const { data, error } = await supabase.functions.invoke("create-bundle-checkout", {
        body: {
          action: "checkout",
          bundle_id: pricing.bundleId,
          zahlart: "rechnung",
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          promoCode: appliedCode || undefined,
          abo_ruhen: aboRuhen ? true : undefined,
          testphase_beenden: testphaseEndet ? true : undefined,
          submission_id: submissionIdRef.current,
          billing_address: {
            name: `${form.first_name} ${form.last_name}`.trim(),
            company: form.company || undefined,
            street: form.street,
            line2: form.line2 || undefined,
            postal_code: form.postal_code,
            city: form.city,
            country: "DE",
          },
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Rechnung erstellt", description: `Rechnungsnummer ${data.invoice_number}. Du erhältst sie per E-Mail.` });
      navigate(`/ausbildung/${pricing.slug}/kauf-erfolgreich?invoice_id=${encodeURIComponent(data.invoice_id)}&bundle_id=${encodeURIComponent(pricing.bundleId)}${dankeZusatz(data)}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      submissionIdRef.current = crypto.randomUUID();
      toast({ title: "Fehler beim Checkout", description: msg, variant: "destructive" });
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-12 max-w-2xl">
      <SEO title={`${pricing.title} – buchen`} description={`Buche die Ausbildung ${pricing.title}.`} path={`/ausbildung/${pricing.slug}/kaufen`} />
      {/* Bild im eigenen Seitenverhältnis — bei fester Höhe wurden Köpfe und
          Kinder angeschnitten. Titel steht darunter statt darüber, damit die
          Szene frei bleibt. */}
      {pricing.image && (
        <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-xl mb-6">
          <img
            src={pricing.image}
            alt={pricing.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <h1 className="text-2xl md:text-4xl font-bold">{pricing.title}</h1>
      <p className="text-muted-foreground mt-2">Wähle deine Zahlungsart und schließe deine Bestellung ab.</p>

      {/* Eckpunkte — kurz erinnern, was man hier eigentlich kauft. */}
      {pricing.highlights && pricing.highlights.length > 0 && (
        <div className="mt-5 mb-8 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          {pricing.highlights.map((highlight) => (
            <div key={highlight} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="text-foreground">{highlight}</span>
            </div>
          ))}
        </div>
      )}
      {!pricing.highlights?.length && <div className="mb-8" />}

      {/* Login-Hinweis für Nicht-Eingeloggte */}
      {!detection.is_authenticated && (
        <Card className="mb-6 border-primary/40 bg-primary/5">
          <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="text-sm">
              <div className="font-semibold">Du bist Mitglied bei MusiKonzept?</div>
              <div className="text-muted-foreground">Logg dich ein, dann gilt hier dein Mitgliedspreis.</div>
            </div>
            <Button asChild variant="outline" size="sm"><a href={loginRedirect}>Jetzt einloggen</a></Button>
          </CardContent>
        </Card>
      )}

      {/* Preis-Header */}
      <Card className="mb-6 border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-baseline gap-3 flex-wrap">
            <span>Dein Preis: {formatEuro(rechnungAmount)}</span>
            {istMitgliedspreis && (
              <span className="text-base font-normal text-muted-foreground line-through">{formatEuro(stdRechnung!)}</span>
            )}
            {zeigtFruehbucher && (
              <span className="text-base font-normal text-muted-foreground line-through">{formatEuro(aktionRegulaerRechnung!)}</span>
            )}
            {!istMitgliedspreis && promoApplied && stdRechnung && stdRechnung !== rechnungAmount && (
              <span className="text-base font-normal text-muted-foreground line-through">{formatEuro(stdRechnung)}</span>
            )}
            {istMitgliedspreis && (
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20">Mitgliedspreis</Badge>
            )}
            {promoApplied && !istMitgliedspreis && (
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20">Aktion aktiv</Badge>
            )}
            {zeigtFruehbucher && (
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20">Frühbucher-Rabatt</Badge>
            )}
          </CardTitle>
          <CardDescription>
            {TARIF_LABELS[detection.tarif]}.
            {zeigtFruehbucher && (
              <div className="mt-2 text-sm text-foreground font-medium">
                Dein Frühbucher-Rabatt: −{formatEuro(fruehbucherRabatt)}
                <span className="ml-2 font-normal text-muted-foreground">
                  (gilt bis {AKTION_ENDE_LABEL})
                </span>
              </div>
            )}
            {(detection.tarif === "extern" || detection.tarif === "standard") && !detection.is_authenticated && (
              <> <a href={loginRedirect} className="underline">Einloggen</a>, um den Mitgliedspreis zu erhalten.</>
            )}
          </CardDescription>
        </CardHeader>
      </Card>

      <form onSubmit={handleSubmit}>
        {/* Zahlungsart */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Zahlungsart</CardTitle>
            <CardDescription>Beide Optionen umfassen denselben Inhalt – kein Aufschlag bei Raten.</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup value={zahlart} onValueChange={(v) => setZahlart(v as BundleZahlart)} className="space-y-3">
              <label className="flex items-start gap-3 rounded-md border p-4 cursor-pointer hover:bg-muted/30">
                <RadioGroupItem value="raten" className="mt-1" />
                <div>
                  <div className="font-medium flex items-baseline gap-2 flex-wrap">
                    <span>{installmentCount} monatliche Raten (SEPA) – {formatEuro(ratenTotal)} gesamt</span>
                    {(promoApplied || istMitgliedspreis) && stdRatenTotal && stdRatenTotal !== ratenTotal && (
                      <span className="text-sm font-normal text-muted-foreground line-through">{formatEuro(stdRatenTotal)} gesamt</span>
                    )}
                    {zeigtFruehbucher && aktionRegulaerRatenTotal !== ratenTotal && (
                      <span className="text-sm font-normal text-muted-foreground line-through">{formatEuro(aktionRegulaerRatenTotal!)} gesamt</span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">{ratenDescription}</div>
                </div>
              </label>
              <label className="flex items-start gap-3 rounded-md border p-4 cursor-pointer hover:bg-muted/30">
                <RadioGroupItem value="rechnung" className="mt-1" />
                <div>
                  <div className="font-medium flex items-baseline gap-2 flex-wrap">
                    <span>Kauf auf Rechnung – {formatEuro(rechnungAmount)}</span>
                    {(promoApplied || istMitgliedspreis) && stdRechnung && stdRechnung !== rechnungAmount && (
                      <span className="text-sm font-normal text-muted-foreground line-through">{formatEuro(stdRechnung)}</span>
                    )}
                    {zeigtFruehbucher && (
                      <span className="text-sm font-normal text-muted-foreground line-through">{formatEuro(aktionRegulaerRechnung!)}</span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">Rechnung per E-Mail, {detection.rechnung.daysUntilDue} Tage Zahlungsziel.</div>
                </div>
              </label>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Gutscheincode */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Tag className="h-4 w-4" /> Gutscheincode</CardTitle>
          </CardHeader>
          <CardContent>
            {appliedCode ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary" />
                  <span className="font-medium">{appliedCode}</span>
                  {promoApplied ? <span className="text-muted-foreground">— Aktion angewendet</span> : <span className="text-muted-foreground">— Code hat keinen Effekt</span>}
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={removeCoupon}><X className="h-4 w-4" /></Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input placeholder="Code eingeben" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} />
                <Button type="button" variant="outline" onClick={applyCoupon} disabled={couponLoading || !couponInput.trim()}>
                  {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Einlösen"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Teilnehmerdaten */}
        <Card className="mb-6">
          <CardHeader><CardTitle>Deine Daten</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div><Label>Vorname *</Label><Input value={form.first_name} onChange={(e) => updateField("first_name", e.target.value)} required /></div>
            <div><Label>Nachname *</Label><Input value={form.last_name} onChange={(e) => updateField("last_name", e.target.value)} required /></div>
            <div className="sm:col-span-2"><Label>E-Mail *</Label><Input type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} required /></div>

            {zahlart === "rechnung" && (
              <>
                <div className="sm:col-span-2"><Label>Firma / Einrichtung (optional)</Label><Input value={form.company} onChange={(e) => updateField("company", e.target.value)} /></div>
                <div className="sm:col-span-2"><Label>Straße & Hausnummer *</Label><Input value={form.street} onChange={(e) => updateField("street", e.target.value)} required /></div>
                <div className="sm:col-span-2"><Label>Adresszusatz</Label><Input value={form.line2} onChange={(e) => updateField("line2", e.target.value)} /></div>
                <div><Label>PLZ *</Label><Input value={form.postal_code} onChange={(e) => updateField("postal_code", e.target.value)} required /></div>
                <div><Label>Ort *</Label><Input value={form.city} onChange={(e) => updateField("city", e.target.value)} required /></div>
              </>
            )}
          </CardContent>
        </Card>

        {/* SEPA-Block */}
        {zahlart === "raten" && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>SEPA-Lastschrift</CardTitle>
              <CardDescription>Die Raten werden monatlich per SEPA-Lastschrift von deinem Konto eingezogen.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Kontoinhaber *</Label>
                <Input value={form.account_holder} onChange={(e) => updateField("account_holder", e.target.value)} required />
              </div>
              <div>
                <Label>IBAN *</Label>
                <div className="mt-1 rounded-md border bg-background px-3 py-2">
                  <IbanElement options={IBAN_ELEMENT_OPTIONS} onChange={(e) => setIbanComplete(e.complete)} />
                </div>
              </div>
              {/* Wortlaut wie in den übrigen Kassen (Abo, Musik-Kita-Paket, Didacta):
                  Stripe wickelt das Mandat als Zahlungsdienstleister ab, eine eigene
                  Gläubiger-ID gibt es dafür nicht. Hier stand vorher eine Platzhalter-Nummer. */}
              <p className="text-xs text-muted-foreground leading-relaxed">
                Mit der Angabe deiner Zahlungsdaten und der Bestätigung dieser Zahlung ermächtigst du MusiKonzept und Stripe,
                unseren Zahlungsdienstleister, Anweisungen an deine Bank zu senden, um dein Konto zu belasten, und deine Bank,
                dein Konto gemäß diesen Anweisungen zu belasten. Du hast Anspruch auf eine Erstattung von deiner Bank gemäß den
                Bedingungen deiner Vereinbarung mit ihr. Eine Erstattung muss innerhalb von 8 Wochen ab dem Datum der Belastung
                deines Kontos beantragt werden.
              </p>
            </CardContent>
          </Card>
        )}

        {aboRuhen && (
          <div className="mb-4 rounded-md border border-primary/40 bg-primary/5 p-4 text-sm">
            <div className="font-semibold mb-1">{RUHEN_TITEL_KASSE}</div>
            <div className="text-muted-foreground">{ruhenSatzKasse(aboRuhen.bis)}</div>
          </div>
        )}

        {testphaseEndet && (
          <div className="mb-4 rounded-md border border-primary/40 bg-primary/5 p-4 text-sm">
            <div className="font-semibold mb-1">{TESTPHASE_TITEL}</div>
            <div className="text-muted-foreground">{testphaseSatzKasse(testphaseEndet.bis)}</div>
          </div>
        )}

        <div className="mb-4 flex items-start gap-3">
          <Checkbox
            id="agb-accept"
            checked={agbAccepted}
            onCheckedChange={(v) => setAgbAccepted(v === true)}
          />
          <label htmlFor="agb-accept" className="text-sm leading-relaxed cursor-pointer">
            Ich habe die{" "}
            <a href="/agb" target="_blank" rel="noopener noreferrer" className="underline decoration-muted-foreground/60 hover:decoration-foreground">
              AGB
            </a>{" "}
            und die{" "}
            <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline decoration-muted-foreground/60 hover:decoration-foreground">
              Datenschutzerklärung
            </a>{" "}
            gelesen und akzeptiere sie.
          </label>
        </div>
        <Button size="lg" type="submit" disabled={loading || !agbAccepted} className="w-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Zahlungspflichtig buchen
        </Button>
        <p className="mt-3 text-center text-xs text-muted-foreground">
          {zahlart === "raten"
            ? "Zahlung per SEPA-Lastschrift über unseren Zahlungsdienstleister Stripe. Deine Bankdaten sehen wir nicht."
            : `Du bekommst die Rechnung per E-Mail, zahlbar innerhalb von ${detection.rechnung.daysUntilDue} Tagen.`}
          {" "}Bei Fragen: <a href="mailto:info@musikonzept.de" className="underline">info@musikonzept.de</a>
        </p>
      </form>
    </div>
  );
}

export default function BundleKaufen() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Elements stripe={stripePromise} options={{ locale: "de" }}>
          <BundleKaufenInner />
        </Elements>
      </main>
      <Footer />
    </div>
  );
}
